"use client";

import { usePathname } from "@/i18n/navigation";
import { commonService } from "@/services/commonService";
import { useSearchParams } from "next/navigation";
import { useEffect } from "react";

const CHAT_SRC =
  "https://api.vivollo.com/chat-widget/widget.js?tenantId=turna&channelId=a0205beb-0ec7-4823-84fe-8645123ebe70";

const DISABLED_ROUTES = [
  "/payment/[cartId]",
  "/bank-three-d-return",
  "/flight/one-way/[airportInfo]/[departureDate]",
  "/flight/round-trip/[airportInfo]/[departureDate]/[returnDate]",
  "/flight/multi-city/[...segments]",
  "/flight/reservation/[reservationId]",

  "/bus-ticket/[landingPageName]",
  "/bus-ticket/one-way/[stationInfo]/[departureDate]",
  "/bus-ticket/reservation/[reservationId]",

  "/ferry/one-way/[stationInfo]/[departureDate]",
  "/ferry/round-trip/[stationInfo]/[departureDate]/[returnDate]",
  "/ferry/reservation/[reservationId]",

  "/hotel/[landingPageName]",
  "/hotel/search/[hotelName]/[checkinDate]/[checkOutDate]",
  "/hotel/reservation/[reservationId]",

  "/car-rental/search/[location]/[dates]",
  "/car-rental/reservation/[cartId]",
  "/account/passengers",
  "/credit-card-points",
];

export default function VivolloChatWidget() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const vivolloChatWidgetUpdateZIndex = () => {
    const applyStyle = (shadowRoot) => {
      if (shadowRoot.querySelector("[data-vivollo-zindex-fix]")) return;
      const style = document.createElement("style");
      style.setAttribute("data-vivollo-zindex-fix", "");
      style.textContent = ".vl\\:z-\\[1000\\] { z-index: 49 !important; }";
      shadowRoot.appendChild(style);
    };

    const el = document.querySelector('[data-vivollo-chat-widget="true"]');
    if (el?.shadowRoot) {
      el.classList.add("vivollo-container");
      applyStyle(el.shadowRoot);
      return null;
    }

    const observer = new MutationObserver((_, obs) => {
      const widget = document.querySelector(
        '[data-vivollo-chat-widget="true"]',
      );
      if (widget?.shadowRoot) {
        applyStyle(widget.shadowRoot);
        obs.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return observer;
  };

  useEffect(() => {
    let zIndexObserver = null;

    const isDisabledRoute = DISABLED_ROUTES.some((route) =>
      pathname.startsWith(route.split("[")[0]),
    );
    const hasHideLayout = searchParams.get("hideLayout") != null;

    if (isDisabledRoute || hasHideLayout) {
      if (window.Vivollo) {
        window.Vivollo.destroy();
      }
      return;
    }

    // Eğer Vivollo zaten yüklüyse sadece reload et
    if (window.Vivollo) {
      window.Vivollo.reload();
      zIndexObserver = vivolloChatWidgetUpdateZIndex();
      return () => {
        zIndexObserver?.disconnect();
      };
    }

    // Script zaten DOM'da varsa z-index fix'i uygula ve çık
    const existingScript = document.querySelector("[data-vivollo-chat-widget]");
    if (existingScript) {
      zIndexObserver = vivolloChatWidgetUpdateZIndex();
      return () => {
        zIndexObserver?.disconnect();
      };
    }

    const handleVivolloLoad = (e) => {
      const vivollo = e.detail.vivollo;

      vivollo.create({
        tenantId: "turna",
        channelId: "a0205beb-0ec7-4823-84fe-8645123ebe70",
        debug: false,
        display: {
          mode: "sticky",
          position: "right",
        },
      });

      vivollo.on("chat:opened", () => {
        const widget = document.querySelector(
          '[data-vivollo-chat-widget="true"]',
        );

        widget?.classList?.add("vivollo-open");
      });

      vivollo.on("chat:minimized", () => {
        const widget = document.querySelector(
          '[data-vivollo-chat-widget="true"]',
        );

        widget?.classList?.remove("vivollo-open");
      });

      vivollo.on("conversation:created", ({ conversation }) => {
        commonService.linkVivolloSession({
          ConversationId: conversation.id,
        });
      });

      zIndexObserver = vivolloChatWidgetUpdateZIndex();
    };

    document.addEventListener("vivollo:load", handleVivolloLoad);

    let fallbackTimer = null;
    let loaded = false;

    const loadWidget = () => {
      if (window?.localStorage) {
        window.localStorage.removeItem("flup.turna.chat.lastBoxState");
      }

      const script = document.createElement("script");
      script.src = CHAT_SRC;
      script.type = "module";
      script.setAttribute("data-vivollo-chat-widget", "");
      document.head.appendChild(script);
    };

    const INTERACTION_EVENTS = [
      "pointerdown",
      "touchstart",
      "scroll",
      "keydown",
    ];
    const listenerOptions = { passive: true, capture: true };

    const removeInteractionListeners = () => {
      INTERACTION_EVENTS.forEach((ev) =>
        window.removeEventListener(ev, triggerLoad, listenerOptions),
      );
    };

    function triggerLoad() {
      if (loaded) return;
      loaded = true;
      removeInteractionListeners();
      if (fallbackTimer !== null) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      loadWidget();
    }

    INTERACTION_EVENTS.forEach((ev) =>
      window.addEventListener(ev, triggerLoad, listenerOptions),
    );
    fallbackTimer = setTimeout(triggerLoad, 15000);

    return () => {
      document.removeEventListener("vivollo:load", handleVivolloLoad);
      zIndexObserver?.disconnect();
      removeInteractionListeners();
      if (fallbackTimer !== null) clearTimeout(fallbackTimer);
    };
  }, [pathname, searchParams]);

  return null;
}
